import { Injectable, inject } from '@angular/core';
import { CanActivate, CanActivateChild, CanActivateFn, ActivatedRouteSnapshot, RouterStateSnapshot, Router, UrlTree } from '@angular/router';
import { Observable, map } from 'rxjs';
import { AuthService } from '../services/auth.service';

@Injectable({ providedIn: 'root' })
export class AuthGuard implements CanActivate, CanActivateChild {
  constructor(private authService: AuthService, private router: Router) {}

  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean | UrlTree | Observable<boolean | UrlTree> {
    return this.checkAccess(route, state);
  }

  canActivateChild(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean | UrlTree | Observable<boolean | UrlTree> {
    return this.checkAccess(route, state);
  }

  private checkAccess(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean | UrlTree | Observable<boolean | UrlTree> {
    // Not logged in → go to login
    if (!this.authService.isLoggedIn) {
      // LOW-2 (audit 2026-06-16): remember where the user was headed (deep-link / bookmark / session
      // expiry) so login can return them there instead of the generic dashboard. Only forward an
      // internal path, never /login itself. Login validates it again before navigating (open-redirect).
      const returnUrl = state.url;
      const extras = (returnUrl && returnUrl !== '/' && !returnUrl.startsWith('/login'))
        ? { queryParams: { returnUrl } } : undefined;
      this.router.navigate(['/login'], extras);
      return false;
    }

    // TC-0126: a role this app has no home for is never redirected (its "home" would refuse it and send it back here,
    // the loop that froze the tab). End the session instead; the sign-in page says the account isn't set up correctly.
    if (!this.authService.hasKnownRole) {
      this.authService.endInvalidAccount();
      return false;
    }

    // TC-0048/0049/4952: a password that must be changed blocks every protected page (the server also refuses the data).
    // Ask the server first, so a stale flag (changed in another tab, or a renewal that raced the change) never forces a
    // second change (TC-0307).
    if (this.authService.currentUser?.mustChangePassword) {
      return this.authService.recheckMustChangePassword().pipe(
        map(still => still ? this.router.createUrlTree(['/change-password']) : this.checkRole(route)));
    }
    return this.checkRole(route);
  }

  private checkRole(route: ActivatedRouteSnapshot): boolean {
    // Check role if route requires specific roles
    const requiredRoles = route.data['roles'] as string[];
    if (requiredRoles && requiredRoles.length > 0) {
      const hasRole = requiredRoles.includes(this.authService.role);
      if (!hasRole) {
        // TC-0126: an unknown role ends the session — never a redirect that could reach this guard again.
        if (!this.authService.hasKnownRole) { this.authService.endInvalidAccount(); return false; }
        // Logged in but wrong role → redirect to their dashboard (each home admits its own role: auth.guard.spec)
        this.authService.redirectAfterLogin();
        return false;
      }
    }

    return true;
  }
}

@Injectable({ providedIn: 'root' })
export class LoginGuard implements CanActivate {
  constructor(private authService: AuthService) {}

  canActivate(): boolean {
    // Already logged in → redirect to dashboard
    if (this.authService.isLoggedIn) {
      this.authService.redirectAfterLogin();
      return false;
    }
    return true;
  }
}

/**
 * Pages outside the app shell that only make sense for a signed-in person (/change-password). Without it a signed-out
 * visitor (Back after an idle sign-out on a shared PC, a bookmark, a typed address) got a page whose calls all end
 * silently (no token, nobody to renew): the Personal email card never loaded and 'Change Password' hung on
 * 'Changing…'. Sends them to the sign-in page instead.
 */
export const signedInGuard: CanActivateFn = () =>
  inject(AuthService).isLoggedIn || inject(Router).parseUrl('/login');
